import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { User, Mail, Lock, Phone, ArrowRight, Loader2, CreditCard } from 'lucide-react';
import { useState } from 'react';

import { api, setAuthToken } from '@/lib/api.ts';
import TurnstileWidget from '@/components/TurnstileWidget.jsx';

export function RegistroClienteForm() {
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [cedulaTipo, setCedulaTipo] = useState('V');
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      nombre: '',
      email: '',
      cedula_numero: '',
      telefono: '',
      password: '',
    },
  });
  const navigate = useNavigate();

  const registro = useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post('/api/auth/register/customer', payload);
      return data;
    },
    onSuccess: (data, variables) => {
      toast.success('Cuenta creada. Revisa tu correo para verificar.');
      if (data?.token) {
        setAuthToken(data.token);
      }
      navigate(`/verificar-correo?email=${encodeURIComponent(variables.email)}`);
    },
    onError: (error) => {
      const message = error.response?.data?.detail ?? 'No se pudo registrar';
      toast.error(message);
    },
  });

  const onSubmit = (values) => {
    // Combinar tipo de cédula + número
    const cedula = `${cedulaTipo}-${values.cedula_numero}`;
    
    // Agregar el token de Turnstile al payload
    const payload = {
      ...values,
      cedula,  // ← Enviar "V-12345678"
      turnstile_token: turnstileToken,
    };
    
    // Remover cedula_numero del payload (ya está en cedula)
    delete payload.cedula_numero;
    
    registro.mutate(payload);
  };

  // --- ESTILOS COMPACTOS (Sin scroll) ---
  const groupClass = "space-y-1.5"; // Espacio reducido
  const labelClass = "block text-[9px] font-bold uppercase tracking-widest text-white/40 ml-1";
  const inputContainerClass = "relative group";
  const inputClass = "w-full h-10 bg-black/40 border border-white/10 rounded-xl pl-10 pr-4 text-sm text-white placeholder-white/20 focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all";
  const iconClass = "absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30 group-focus-within:text-cyan-400 transition-colors";
  const errorClass = "text-[10px] text-red-400 mt-0.5 ml-1";

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      
      {/* GRID 2 COLUMNAS COMPACTO */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        
        {/* Nombre */}
        <div className={groupClass}>
          <label className={labelClass}>Nombre Completo</label>
          <div className={inputContainerClass}>
            <User className={iconClass} />
            <input
              type="text"
              placeholder="Tu nombre"
              className={inputClass}
              {...register('nombre', { required: 'Requerido' })}
            />
          </div>
          {errors.nombre && <p className={errorClass}>{errors.nombre.message}</p>}
        </div>

        {/* Email */}
        <div className={groupClass}>
          <label className={labelClass}>Correo Electrónico</label>
          <div className={inputContainerClass}>
            <Mail className={iconClass} />
            <input
              type="email"
              placeholder="tu@email.com"
              className={inputClass}
              {...register('email', {
                required: 'Requerido',
                pattern: { value: /^\S+@\S+$/i, message: 'Email inválido' }
              })}
            />
          </div>
          {errors.email && <p className={errorClass}>{errors.email.message}</p>}
        </div>

        {/* Cédula / ID */}
        <div className={groupClass}>
          <label className={labelClass}>Cédula / Identificación</label>
          <div className="flex gap-2">
            {/* Select para tipo (V/J/E) */}
            <select
              value={cedulaTipo}
              onChange={(e) => setCedulaTipo(e.target.value)}
              className="h-10 bg-black/40 border border-white/10 rounded-xl px-3 text-sm text-white focus:outline-none focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/20 transition-all"
            >
              <option value="V">V</option>
              <option value="J">J</option>
              <option value="E">E</option>
            </select>
            
            {/* Input para número */}
            <div className="flex-1 relative group">
              <CreditCard className={iconClass} />
              <input
                type="text"
                placeholder="12345678"
                className={inputClass}
                {...register('cedula_numero', {
                  required: 'Requerido',
                  pattern: { value: /^[0-9]+$/, message: 'Solo números' }
                })}
              />
            </div>
          </div>
          {errors.cedula_numero && <p className={errorClass}>{errors.cedula_numero.message}</p>}
        </div>

        {/* Teléfono (AHORA OBLIGATORIO) */}
        <div className={groupClass}>
          <label className={labelClass}>Teléfono</label>
          <div className={inputContainerClass}>
            <Phone className={iconClass} />
            <input
              type="tel"
              placeholder="+58 412 123 4567"
              className={inputClass}
              {...register('telefono', { required: 'Requerido' })}
            />
          </div>
          {errors.telefono && <p className={errorClass}>{errors.telefono.message}</p>}
        </div>

        {/* Contraseña (Ocupa todo el ancho en móvil, o su celda en desktop) */}
        <div className={`${groupClass} md:col-span-2`}>
          <label className={labelClass}>Contraseña</label>
          <div className={inputContainerClass}>
            <Lock className={iconClass} />
            <input
              type="password"
              placeholder="Mínimo 8 caracteres"
              className={inputClass}
              {...register('password', {
                required: 'Requerido',
                minLength: { value: 8, message: 'Mínimo 8 caracteres' }
              })}
            />
          </div>
          {errors.password && <p className={errorClass}>{errors.password.message}</p>}
        </div>

      </div>

      {/* Widget de Turnstile (Captcha) */}
      <TurnstileWidget
        onVerify={(token) => setTurnstileToken(token)}
        onError={() => setTurnstileToken(null)}
        onExpire={() => setTurnstileToken(null)}
      />

      {/* Botón Acción (Gradiente Cian) - Más compacto */}
      <button
        type="submit"
        disabled={registro.isPending}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold uppercase tracking-widest text-xs shadow-[0_0_20px_rgba(6,182,212,0.3)] transition-all transform hover:scale-[1.01] disabled:opacity-70 disabled:cursor-not-allowed border border-white/10"
      >
        {registro.isPending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Registrando...
          </>
        ) : (
          <>
            Crear Cuenta <ArrowRight className="h-4 w-4" />
          </>
        )}
      </button>

      <p className="text-[9px] text-center text-white/30 leading-relaxed">
        Al registrarte, aceptas nuestros Términos y Condiciones. <br/>
        Recibirás un enlace de activación en tu correo.
      </p>
    </form>
  );
}